import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Heading,
  Input,
  Textarea,
  VStack,
  Text,
  useToast,
  Spinner,
  HStack,
  Radio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { saveWorkshopRegistration, sendConfirmationEmail } from '../../services/workshopService';
import { useAnalytics } from '../../hooks/useAnalytics';

const MotionBox = motion(Box);

/**
 * Workshop Registration Form Component
 * Handles form submission, validation, and Firebase operations
 */
const RegistrationForm = ({ workshopId, workshop, location, influencerId = '', flags = '' }) => {
  const toast = useToast();
  const { trackEvent } = useAnalytics();

  // Whether each attendance mode is actually offered for this workshop
  const canAttendOnline = !!workshop?.online;
  const canAttendInPerson = !!location;
  const requiresAttendanceChoice = canAttendOnline && canAttendInPerson;
  const defaultAttendanceMode = requiresAttendanceChoice
    ? ''
    : canAttendOnline
    ? 'online'
    : 'in-person';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    attendanceMode: defaultAttendanceMode,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedAttendanceMode, setSubmittedAttendanceMode] = useState(defaultAttendanceMode);

  if (!workshop) return null;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    }

    if (requiresAttendanceChoice && !formData.attendanceMode) {
      newErrors.attendanceMode = 'Please choose how you will attend';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      phone: value,
    }));
    if (errors.phone) {
      setErrors((prev) => ({
        ...prev,
        phone: '',
      }));
    }
  };

  const handleAttendanceModeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      attendanceMode: value,
    }));
    if (errors.attendanceMode) {
      setErrors((prev) => ({
        ...prev,
        attendanceMode: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors in the form',
        status: 'error',
        duration: 3,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Save registration to Firebase
      const registrationId = await saveWorkshopRegistration(workshopId, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        attendanceMode: formData.attendanceMode,
        influencerId,
        flags,
      });

      console.log('Registration saved:', registrationId);

      // Track signup event
      trackEvent('signup', {
        workshopId,
        registrationId,
        email: formData.email,
        influencerId,
      });

      // Send confirmation email (fire and forget)
      /*
      const emailResult = await sendConfirmationEmail(workshopId, {
        email: formData.email,
        name: formData.name,
        workshopTitle: workshop.title,
        workshopDate: workshop.schedule.date,
        workshopTime: workshop.schedule.startTime,
        inClassLocation: location?.name || 'TBD',
        zoomLink: workshop.online ? 'Check your email for meeting link' : '',
        zoomPassword: '',
        instructorName: workshop.instructor?.name || '',
      });

      console.log('Email result:', emailResult);
      */

      // Show success
      setSubmittedAttendanceMode(formData.attendanceMode);
      setIsSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '', attendanceMode: defaultAttendanceMode });

      toast({
        title: 'Registration Successful! 🎉',
        description: 'Check your email for confirmation details',
        status: 'success',
        duration: 5,
        isClosable: true,
      });

      // Scroll to success message
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 500);
    } catch (error) {
      console.error('Registration error:', error);

      trackEvent('registration_error', {
        workshopId: workshop.title,
        error: error.message,
      });

      toast({
        title: 'Registration Failed',
        description: error.message || 'Something went wrong. Please try again.',
        status: 'error',
        duration: 5,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <Box py={{ base: 16, md: 20 }} bg="green.50">
        <Container maxW="container.sm">
          <MotionBox
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <VStack spacing={6} textAlign="center">
              <Text fontSize="5xl">🎉</Text>
              <Heading as="h2" size="lg" color="green.700">
                You're all set!
              </Heading>
              <Text color="gray.700" fontSize="md" lineHeight="1.8">
                Thank you for registering. Check your email for:
              </Text>
              <VStack align="start" spacing={2} bg="white" p={6} borderRadius="lg" w="full">
                <Text>✓ Workshop date and time confirmation</Text>
                {submittedAttendanceMode === 'online' && (
                  <Text>✓ Meeting link for online attendance</Text>
                )}
                {submittedAttendanceMode === 'in-person' && (
                  <Text>✓ Location details for in-class attendance</Text>
                )}
                <Text>✓ Instructor bio and resources</Text>
              </VStack>
              <Button
                colorScheme="green"
                size="lg"
                w="full"
                onClick={() => (window.location.href = '/workshops')}
              >
                Browse Other Workshops
              </Button>
            </VStack>
          </MotionBox>
        </Container>
      </Box>
    );
  }

  return (
    <Box py={{ base: 16, md: 20 }} bg="white" id="register">
      <Container maxW="container.sm">
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <VStack spacing={8}>
            {/* Header */}
            <VStack align="center" spacing={3} textAlign="center">
              <Heading as="h2" size="xl" fontWeight="900" color="gray.900">
                Register Now
              </Heading>
              <Text color="gray.600">
                Secure your spot for this free workshop
              </Text>
            </VStack>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <VStack spacing={6}>
                {/* Name */}
                <FormControl isInvalid={!!errors.name}>
                  <FormLabel fontWeight="bold" color="gray.700">
                    Full Name
                  </FormLabel>
                  <Input
                    name="name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    size="lg"
                    borderRadius="lg"
                  />
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                </FormControl>

                {/* Email */}
                <FormControl isInvalid={!!errors.email}>
                  <FormLabel fontWeight="bold" color="gray.700">
                    Email Address
                  </FormLabel>
                  <Input
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    size="lg"
                    borderRadius="lg"
                  />
                  <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>

                {/* Phone */}
                <FormControl isInvalid={!!errors.phone}>
                  <FormLabel fontWeight="bold" color="gray.700">
                    Phone Number
                  </FormLabel>
                  <Box
                    className="phone-input-wrapper"
                    sx={{
                      '& input': {
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '0.5rem',
                        border: '1px solid #E2E8F0',
                        fontSize: '1rem',
                        _focus: {
                          borderColor: '#667eea',
                          outline: 'none',
                        },
                      },
                    }}
                  >
                    <PhoneInput
                      international
                      countryCallingCodeEditable={false}
                      defaultCountry="IN"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="+91 9876543210"
                    />
                  </Box>
                  <FormErrorMessage>{errors.phone}</FormErrorMessage>
                </FormControl>

                {/* Attendance Mode */}
                {requiresAttendanceChoice && (
                  <FormControl isInvalid={!!errors.attendanceMode}>
                    <FormLabel fontWeight="bold" color="gray.700">
                      How will you attend?
                    </FormLabel>
                    <RadioGroup
                      value={formData.attendanceMode}
                      onChange={handleAttendanceModeChange}
                    >
                      <Stack direction={{ base: 'column', sm: 'row' }} spacing={6}>
                        <Radio value="in-person" colorScheme="purple">
                          In-Person{location?.city ? ` (${location.city})` : ''}
                        </Radio>
                        <Radio value="online" colorScheme="purple">
                          Online
                        </Radio>
                      </Stack>
                    </RadioGroup>
                    <FormErrorMessage>{errors.attendanceMode}</FormErrorMessage>
                  </FormControl>
                )}

                {/* Message */}
                <FormControl>
                  <FormLabel fontWeight="bold" color="gray.700">
                    Message (Optional)
                  </FormLabel>
                  <Textarea
                    name="message"
                    placeholder="Any questions or comments?"
                    value={formData.message}
                    onChange={handleChange}
                    size="lg"
                    borderRadius="lg"
                    rows={4}
                  />
                </FormControl>

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  width="100%"
                  colorScheme="purple"
                  fontWeight="bold"
                  isLoading={isSubmitting}
                  loadingText="Registering..."
                >
                  Register for Workshop
                </Button>

                {/* Terms */}
                <Text fontSize="xs" color="gray.500" textAlign="center">
                  By registering, you agree to our{' '}
                  <Box as="a" href="/terms-of-service" color="purple.600" _hover={{ underline: 'underline' }}>
                    terms of service
                  </Box>{' '}
                  and{' '}
                  <Box as="a" href="/privacy-policy" color="purple.600" _hover={{ underline: 'underline' }}>
                    privacy policy
                  </Box>
                </Text>
              </VStack>
            </form>
          </VStack>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default RegistrationForm;
